import React, { useEffect } from "react";
import { Sparkles, Share2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { gam, levelColor } from "../lib/gamificationApi";
import { toast } from "sonner";

/**
 * Celebratory level-up modal. Shown when the WS pushes `level:up` or when
 * triggered manually. Offers an opt-in "Share to feed" CTA that creates a
 * milestone post via /api/gamification/share-levelup.
 */
export default function LevelUpModal({ open, onClose, before, after }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        // simple haptic / sound could go here
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || !after) return null;
    const color = levelColor(after.slug);

    const handleShare = async () => {
        try {
            await gam.shareLevelup(after.slug);
            toast.success("Posted to your feed");
            onClose();
        } catch {
            toast.error("Couldn't post — try again from your profile.");
        }
    };

    return (
        <div data-testid="level-up-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight-pine/80 backdrop-blur-sm">
            <button onClick={onClose} aria-label="Close" data-testid="level-up-close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-snowcap/20 hover:bg-snowcap/40 flex items-center justify-center text-white">
                <X className="w-5 h-5" />
            </button>
            <div className="relative w-full max-w-md bg-snowcap rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                {/* burst */}
                <div className="absolute inset-0 pointer-events-none opacity-20" style={{ background: `radial-gradient(circle at 50% 0%, ${color} 0%, transparent 60%)` }} />
                <div className="relative p-8 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 shadow-lg" style={{ backgroundColor: color }}>
                        <Sparkles className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-gray mb-1">Level up</div>
                    <h2 data-testid="level-up-name" className="font-display font-black text-3xl text-midnight-pine tracking-tight mb-1">{after.name}</h2>
                    {after.tagline && <p className="text-sm text-granite italic">"{after.tagline}"</p>}

                    {before?.name && before.name !== after.name && (
                        <div className="mt-5 text-xs text-slate-gray">
                            Promoted from <span className="font-bold text-granite">{before.name}</span>
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-2 gap-2">
                        <button
                            onClick={handleShare}
                            data-testid="level-up-share-btn"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-ember-orange hover:bg-ember-dark text-white font-bold text-sm active:scale-95 transition-all"
                        >
                            <Share2 className="w-4 h-4" /> Share to feed
                        </button>
                        <Link
                            to="/passport"
                            onClick={onClose}
                            data-testid="level-up-passport-btn"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-snowcap border border-ridge-line text-granite font-bold text-sm hover:border-midnight-pine active:scale-95 transition-all"
                        >
                            View Passport
                        </Link>
                    </div>
                    <button onClick={onClose} data-testid="level-up-dismiss" className="text-xs text-slate-gray hover:text-granite mt-3">
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
}
